
import { laptopDataSenti } from "@/data/laptopDataSenti";
import type { Device, FilterDevice } from "@/interfaces/types";
import axios from "axios";
import { toRefs } from "vue";
import { timeZone } from "./timeZone";
import { environment } from "@/environment/environment";
import { accessData } from "@/data/accessData";
import { userData } from "@/data/userData";
import { storeToRefs } from "pinia";

const { apiUrl: baseUrl } = environment


const { adjustTimeToTimeZone, getTimeZone } = timeZone()

export function laptopSentiRequest(){

    const deviceStore = laptopDataSenti()
    const { devices } = toRefs(deviceStore)

    const accessStore = accessData()
    const { decommissionedDevPopup } = toRefs(accessStore)

    const userStore = userData()
    const {user} = storeToRefs(userStore)


    const initiateScan = async (uuid: string) => {
        try {
            const response = await axios.post(`${baseUrl}/initiateScan`, {group_id: user.value.groupId, uuid: uuid});
            return response.data
        } catch (errors) {
            console.error('Unknown error:', errors);
            return 0
        }
    };

    const abortScan = async (uuid: string) => {
        try {
            const response = await axios.post(`${baseUrl}/abortScan`, {group_id: user.value.groupId, uuid: uuid});
            return response.data
        } catch (errors) {
            console.error('Unknown error:', errors);
            return 0
        }
    }

    const getDevicesData = async (): Promise<boolean> => {
        try {
            const response = await axios.get(`${baseUrl}/getDevicesData`,
                {
                params: {
                    groupId: user.value.groupId
                }
            });
            const responseDevices = response.data as Device[]
            const adjustData = await adjustDateToTimeZoneUser(responseDevices)
            
            decommissionedDevPopup.value = adjustData.some(dev => dev.isDecommissioned)
            devices.value = adjustData
            return !!devices.value
        } catch (errors) {
            console.log("🚀 ~ getDevicesData ~ errors from Group ID:", user.value.groupId, errors)
            return false
        }
    };

    const GET_DEVICES_QUERY = `
    query GetDevices($groupId: String!) {
        getDevicesData(groupId: $groupId) {
            id
            icon
            
            computerName
            modelName
            osName
            
            agentVersion
            coreCount
            cpuCount
            cpuId
            lastIpToMgmt
            installerType
            osArch
            osRevision
            customerId
            uuid
            activeThreats
            scanStatus
            isActive
            networkStatus
            operationalState
            operationalStateExpiration
            
            lastActiveDate
            fullDiskScanLastUpdatedAt
            scanStartedAt
            scanAbortedAt
            scanFinishedAt
            registeredAt

            scanStatusToggle
            isDecommissioned
            selected
        }
    }
    `;

    const getDevicesDataGQL = async (): Promise<boolean> => {
        try {
            const response = await axios.post(`${baseUrl}/graphql`,
                {
                    query: GET_DEVICES_QUERY,
                    variables: {
                        groupId: user.value.groupId,
                    },
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );

            if (response.data.errors) {
                console.error( 'GraphQL errors:', response.data.errors);
                return false
            }

            const responseDevices = response.data.data.getDevicesData;
            const adjustData = await adjustDateToTimeZoneUser(responseDevices);

            decommissionedDevPopup.value = adjustData.some(
                (dev) => dev.isDecommissioned,
            );
            devices.value = [...adjustData];
            return true
        } catch (errors) {
            console.log('🚀 ~ getDevicesData ~ errors from Group ID:',user.value.groupId,errors);
            return false
        }
    };

    // TODO
    // call this method after scan start/stop => update the state of the scan status (reactivity)
    // const updateInfoDevices = async (uuids: string[]) => {
    //     const data = await getUserData()
    //     if(data.error){
    //         router.push('/landingPage')
    //         return
    //     }
    //     try {
    //         const response = await axios.get(`${baseUrl}/updateInfoDevices`,
    //             {
    //             params: {
    //                 uuids: uuids
    //             }
    //         });
    //         const responseDevices = response.data as Device
    //         devices.value.map(dev => {
    //             if(dev.uuid === uuids[0]){
    //                 dev = responseDevices
    //             }
    //         })
            
    //         return responseDevices
    //     } catch (errors) {
    //         console.log("🚀 ~ getDevicesData ~ errors from Group ID:", data.groupId, errors)
    //         return 
    //     }
    // };

    const getDeviceUuids = async (groupId: string): Promise<string[]> => {
        try {
            const response = await axios.get(`${baseUrl}/getUuids`,
                {
                params: {
                    groupId: groupId
                }
            });
            return response.data
        } catch (errors) {
            console.log("🚀 ~ getDeviceUuids ~ errors from Group ID:", groupId, errors)
            return []
        }
    };

    /*
     Usefull to get device's informations even if sentinelone was uninstalled from a device
     This kind of data are needed e.g. in the activity FE component => filter activity through the Multiselect component
     */
    const getDevicesDatabase = async (): Promise<FilterDevice[]> => {
        try {
            const response = await axios.get(`${baseUrl}/getDevicesDatabase`,
                {
                params: {
                    userId: user.value.id
                }
            });
            return response.data as FilterDevice[]
        } catch (errors) {
            console.log("🚀 ~ getDeviceDatabase ~ errors:", errors)
            return []
        }
    };


    const getDevicesCount = async (): Promise<number|null> => {
        try {
            const response = await axios.get(`${baseUrl}/getDevicesCount`, {
                params: { groupId: user.value.groupId}
            });
            return response.data
        } catch (errors) {
            console.log("🚀 ~ getDevicesCount ~ errors:", errors)
            return null
        }
    };

   /**
   * Sentinel one API returns the UTC (Greenwich)
   * For the end user we have to calculate the right time depending on where they are located
   */
    async function adjustDateToTimeZoneUser(responseDevices: Device[]): Promise<Device[]>{
        const tz = getTimeZone()
        return responseDevices.map(dev => ({
            ...dev,
            isFreeSlot: false,
            fullDiskScanLastUpdatedAt: dev.fullDiskScanLastUpdatedAt ? adjustTimeToTimeZone(dev.fullDiskScanLastUpdatedAt, tz) : '',
            scanStartedAt: dev.scanStartedAt ? adjustTimeToTimeZone(dev.scanStartedAt, tz) : '',
            scanAbortedAt: dev.scanAbortedAt ? adjustTimeToTimeZone(dev.scanAbortedAt, tz) : '',
            scanFinishedAt: dev.scanFinishedAt ? adjustTimeToTimeZone(dev.scanFinishedAt, tz) : '',
            lastActiveDate: dev.lastActiveDate ? adjustTimeToTimeZone(dev.lastActiveDate, tz) : '',
            registeredAt: dev.registeredAt ? adjustTimeToTimeZone(dev.registeredAt, tz) : '',
            operationalStateExpiration: dev.operationalStateExpiration ? adjustTimeToTimeZone(dev.operationalStateExpiration, tz) : '',

        }))
    }

    return {
        initiateScan,
        getDevicesData,
        abortScan,
        getDevicesDatabase,
        getDeviceUuids,
        getDevicesCount,
        adjustDateToTimeZoneUser,
        getDevicesDataGQL
    }
}